import { DotMatrix } from '@clickguard/design-system'
import { useEffect, useState, useCallback, useRef } from 'react'
import { GButton, GInput, GTooltip } from 'components/basic-blocks'
import { GRadio } from '../../../../../components/basic-blocks/g-radio'
import { trackingScript } from '../../../../../config/tracking-code'
import { AppConfig } from 'config'
import { ShopifyConnectSettings } from 'components/shopify-install'
import { useFeatureFlag } from 'services/feature-flags'
import { AdAccountLinks, ShopifyLinkStatusDTO } from 'services/user/ad-account-links'
import { useStores } from '../../../../../hooks'
import { GtmAccountSelector } from '../../gtm'
import { AiOutlineQuestionCircle } from 'react-icons/ai'
import { useTrackPostHogEvent } from '../../../../../utils/posthog'
import { PostHogEvent, PostHogPath } from '../../../../../utils/posthog-events'
import { GTooltip as GCopyTooltip } from 'components/basic-blocks'
import { FiCopy } from 'react-icons/fi'
import { RiCheckLine } from 'react-icons/ri'
import { PiWarningDiamondFill } from 'react-icons/pi'
import { HiExternalLink } from 'react-icons/hi'
import { useState as useLocalState } from 'react'
import { isValidDomain } from '../../../../../utils'
import { Assets } from '../../../../../services/user/assets'
import { toast } from 'react-toastify'

export const StandardTrackingSection = ({
	onVerificationSuccess,
	hideVerification = false
}: {
	onVerificationSuccess?: () => void
	hideVerification?: boolean
}) => {
	const {
		assetStore: {
			asset,
			gtmTag,
			getGtmTag,
			getDomainForScripts,
			setAssetForId
		},
		accountStore: { account, getGtmLinks, hasFeature }
	} = useStores()
	const trackPostHogEvent = useTrackPostHogEvent()
	const shopifyEnabled = useFeatureFlag('shopify_enabled')

	// Starts null (nothing selected) so we don't flash a default before the async GTM/Shopify checks
	// resolve; falls back to MANUAL once those complete without a live connection.
	const [installType, setInstallType] = useState<'GTM' | 'MANUAL' | 'SHOPIFY' | 'EMAIL' | null>(null)
	const [shopifyLink, setShopifyLink] = useState<ShopifyLinkStatusDTO | null | undefined>(undefined)
	const [developerEmail, setDeveloperEmail] = useState('')
	const [websiteDomain, setWebsiteDomain] = useState(asset?.domain || '')
	const [domainError, setDomainError] = useState<string | null>(null)
	const [isVerifying, setIsVerifying] = useState(false)
	const [verificationStatus, setVerificationStatus] = useState<
		'pending' | 'verified' | 'error' | null
	>(null)
	const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null)
	const timeoutRef = useRef<NodeJS.Timeout | null>(null)

	const hasAccessToServerSideTracking = hasFeature('server_side_tracking')

	const [isTrackingVerificationDone, setIsTrackingVerificationDone] = useState(
		asset?.server_side_tracking?.verification?.eventReceived === true
	)
	const [serverSideTrackingUrlDetected, setServerSideTrackingUrlDetected] =
		useState(
			hasAccessToServerSideTracking
				? asset?.server_side_tracking?.verification
						?.serverSideTrackingUrlDetected === true
				: true
		)
	const [trackingVerificationCompletedAt, setTrackingVerificationCompletedAt] =
		useState<Date | null>(null)

	const hasDoneServerSideTracking =
		asset?.server_side_tracking?.setupCompleted === true

	const domainForScripts =
		asset && hasAccessToServerSideTracking
			? getDomainForScripts(asset)
			: undefined

	useEffect(() => {
		gtmTag && setInstallType('GTM')
	}, [gtmTag])

	// Remember a Shopify connection the same way GTM is remembered — default the method to SHOPIFY
	// when this asset already has a live Shopify link. The fetched status is handed to
	// ShopifyConnectSettings so it doesn't re-fetch (no second loader).
	useEffect(() => {
		let cancelled = false
		if (account?.id && asset?.id) {
			AdAccountLinks.getShopifyLink(account.id, asset.id)
				.then((link) => {
					if (cancelled) return
					setShopifyLink(link)
					// live link → select Shopify; otherwise settle on MANUAL unless GTM already claimed it.
					if (link) setInstallType('SHOPIFY')
					else setInstallType((prev) => prev ?? 'MANUAL')
				})
				.catch(() => {
					if (cancelled) return
					setShopifyLink(null)
					setInstallType((prev) => prev ?? 'MANUAL')
				})
		}
		return () => {
			cancelled = true
		}
	}, [account?.id, asset?.id])

	useEffect(() => {
		if (onVerificationSuccess && isTrackingVerificationDone) {
			onVerificationSuccess()
		}
	}, [isTrackingVerificationDone])

	useEffect(() => {
		if (account?.id) {
			getGtmLinks(account.id)
			asset?.id && getGtmTag(account.id, asset.id)
		}
		if (asset?.server_side_tracking?.verification?.eventReceived === true) {
			setIsTrackingVerificationDone(true)
		} else {
			setIsTrackingVerificationDone(false)
		}
		if (
			asset?.server_side_tracking?.verification
				?.serverSideTrackingUrlDetected === true
		) {
			setServerSideTrackingUrlDetected(true)
		} else {
			setServerSideTrackingUrlDetected(
				hasAccessToServerSideTracking ? false : true
			)
		}
		if (asset?.server_side_tracking?.verification?.verifiedAt) {
			setTrackingVerificationCompletedAt(
				new Date(asset.server_side_tracking.verification.verifiedAt)
			)
		} else {
			setTrackingVerificationCompletedAt(null)
		}
	}, [asset?.id, account?.id, asset?.server_side_tracking?.verification])

	// Cleanup polling and timeout on unmount
	useEffect(() => {
		return () => {
			if (pollingIntervalRef.current) {
				clearInterval(pollingIntervalRef.current)
			}
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current)
			}
		}
	}, [])

	const handleVerifyInstallation = useCallback(async () => {
		// Validate domain before verification
		if (!websiteDomain || !isValidDomain(websiteDomain)) {
			setDomainError('Please enter a valid domain (e.g., example.com)')
			return
		}

		if (!account?.id || !asset?.id) {
			toast.error('Account or asset information is missing')
			return
		}

		setDomainError(null)
		setIsVerifying(true)
		setVerificationStatus('pending')

		try {
			// Start verification - backend will generate and return the verification parameter
			const result = await Assets.startVerification(account.id, asset.id)
			const verificationParam = result.param

			// Open new tab with verification parameter
			const url = websiteDomain.startsWith('http')
				? `${websiteDomain}?cgvrfc=${verificationParam}`
				: `https://${websiteDomain}?cgvrfc=${verificationParam}`
			window.open(url, '_blank')

			// Start polling every 30 seconds
			let pollCount = 0
			const maxPolls = 5 // 5 polls = 2.5 minutes (30s * 5 = 150s)

			pollingIntervalRef.current = setInterval(async () => {
				try {
					pollCount++
					const result = await Assets.checkVerificationStatus(
						account.id,
						asset.id
					)
					if (result.verification?.eventReceived === true) {
						setVerificationStatus('verified')
						setIsVerifying(false)
						if (result.verification.verifiedAt) {
							setTrackingVerificationCompletedAt(
								new Date(result.verification.verifiedAt)
							)
						}
						setIsTrackingVerificationDone(true)
						if (result.verification?.serverSideTrackingUrlDetected === true) {
							setServerSideTrackingUrlDetected(true)
						} else {
							setServerSideTrackingUrlDetected(false)
						}
						if (pollingIntervalRef.current) {
							clearInterval(pollingIntervalRef.current)
						}
						if (timeoutRef.current) {
							clearTimeout(timeoutRef.current)
						}
						await setAssetForId(account.id, asset.id)

						// Track PostHog event for completing tracking code step
						// Check if we're in onboarding/setup flow
						const isInSetup =
							account?.status === 'SETUP' ||
							account?.setup?.step !== 'SETUP_COMPLETE'
						if (isInSetup) {
							const method =
								installType === 'GTM'
									? 'google_tag_manager'
									: installType === 'MANUAL'
									? 'manual'
									: 'email_developer'
							trackPostHogEvent(
								PostHogEvent.ONBOARDING_COMPLETE_TC_STEP,
								PostHogPath.ONBOARDING,
								{
									server_side_tracking: false,
									tc_status: 'success',
									method: method
								}
							)
						}

						toast.success(
							'Verification successful! The tracker script is installed.'
						)
						if (onVerificationSuccess) {
							onVerificationSuccess()
						}
					} else if (pollCount >= maxPolls) {
						// Timeout reached
						setVerificationStatus('error')
						setIsVerifying(false)

						// Track PostHog event for failed verification
						const isInSetup =
							account?.status === 'SETUP' ||
							account?.setup?.step !== 'SETUP_COMPLETE'
						if (isInSetup) {
							const method =
								installType === 'GTM'
									? 'google_tag_manager'
									: installType === 'MANUAL'
									? 'manual'
									: 'email_developer'
							trackPostHogEvent(
								PostHogEvent.ONBOARDING_COMPLETE_TC_STEP,
								PostHogPath.ONBOARDING,
								{
									server_side_tracking: false,
									tc_status: 'failed',
									method: method
								}
							)
						}

						if (pollingIntervalRef.current) {
							clearInterval(pollingIntervalRef.current)
						}
						if (timeoutRef.current) {
							clearTimeout(timeoutRef.current)
						}
						toast.error(
							'Verification failed. Please make sure you have the tracker script installed on your website.'
						)
					}
				} catch (error) {
					console.error('Error checking verification status:', error)
					// On error, continue polling unless we've reached max polls
					if (pollCount >= maxPolls) {
						setVerificationStatus('error')
						setIsVerifying(false)
						if (pollingIntervalRef.current) {
							clearInterval(pollingIntervalRef.current)
						}
						if (timeoutRef.current) {
							clearTimeout(timeoutRef.current)
						}
						toast.error(
							'Verification failed. Please make sure you have the tracker script installed on your website.'
						)
					}
				}
			}, 15000) // Poll every 15 seconds

			// Set timeout for 2.5 minutes as backup
			timeoutRef.current = setTimeout(() => {
				setVerificationStatus('error')
				setIsVerifying(false)

				// Track PostHog event for failed verification
				const isInSetup =
					account?.status === 'SETUP' ||
					account?.setup?.step !== 'SETUP_COMPLETE'
				if (isInSetup) {
					const method =
						installType === 'GTM'
							? 'google_tag_manager'
							: installType === 'MANUAL'
							? 'manual'
							: 'email_developer'
					trackPostHogEvent(
						PostHogEvent.ONBOARDING_COMPLETE_TC_STEP,
						PostHogPath.ONBOARDING,
						{
							server_side_tracking: false,
							tc_status: 'failed',
							method: method
						}
					)
				}

				if (pollingIntervalRef.current) {
					clearInterval(pollingIntervalRef.current)
				}
				toast.error(
					'Verification failed. Please make sure you have the tracker script installed on your website.'
				)
			}, 150000) // 2.5 minutes
		} catch (error: any) {
			console.error('Verification error:', error)
			setVerificationStatus('error')
			setIsVerifying(false)
			toast.error('Failed to start verification. Please try again.')
		}
	}, [websiteDomain, account?.id, asset?.id, verificationStatus])

	return (
		<div>
			<div className="mb-4 lg:mb-6">
				<h1 className="text-xl font-medium">Tracking code installation</h1>
			</div>
			<p className="text-t-checkbox-unactive text-base">
				Setting up tracking code is required for tracking traffic and visitor
				behavior on your website.
			</p>
			{installType === null ? (
				<div className="my-6 flex items-center gap-3">
					<DotMatrix size="sm" tone="inherit" className="text-primary-500" />
					<span className="text-t-checkbox-unactive text-base">Checking your tracking setup…</span>
				</div>
			) : (
			<div className="my-6">
				<div className="flex items-center gap-1">
					<GRadio
						value={installType ?? ''}
						checked={installType === 'GTM'}
						onChange={() => setInstallType('GTM')}
						labelClass="font-normal text-t-google-tooltip text-base"
						label="Install with Google Tag Manager"
						name="gtm"
					/>
					<GTooltip
						position="top"
						arrowClassName="w-0 h-0 absolute bottom-[0.5px] border-l-8 border-l-transparent  border-r-8 border-r-transparent border-t-8 border-t-tooltip-caret"
						contentClassName="border-none font-grotesk mb-2 bg-t-smoke w-[300px] font-medium text-sm text-t-desc-gray"
						content="Install TC with Clickguard with Google Tag Manager"
					>
						<AiOutlineQuestionCircle className="text-t-gray-hold h-4 w-4" />
					</GTooltip>
				</div>
				<div className="flex items-center gap-1 mt-3">
					<GRadio
						value={installType ?? ''}
						checked={installType === 'MANUAL'}
						onChange={() => setInstallType('MANUAL')}
						labelClass="font-normal text-t-google-tooltip text-base"
						label="Install manually"
						name="manual"
					/>
					<GTooltip
						position="top"
						arrowClassName="w-0 h-0 absolute bottom-[0.5px] border-l-8 border-l-transparent  border-r-8 border-r-transparent border-t-8 border-t-tooltip-caret"
						contentClassName="border-none font-grotesk mb-2 bg-t-smoke w-[300px] font-medium text-sm text-t-desc-gray"
						content="To install manually, put the following code in the <head> section of your website"
					>
						<AiOutlineQuestionCircle className="text-t-gray-hold h-4 w-4" />
					</GTooltip>
				</div>
				{shopifyEnabled && (
					<div className="flex items-center gap-1 mt-3">
						<GRadio
							value={installType ?? ''}
							checked={installType === 'SHOPIFY'}
							onChange={() => setInstallType('SHOPIFY')}
							labelClass="font-normal text-t-google-tooltip text-base"
							label="Install on Shopify Store"
							name="shopify"
						/>
						<GTooltip
							position="top"
							arrowClassName="w-0 h-0 absolute bottom-[0.5px] border-l-8 border-l-transparent  border-r-8 border-r-transparent border-t-8 border-t-t-smoke"
							contentClassName="border-none font-grotesk mb-2 bg-t-smoke w-[300px] font-medium text-sm text-t-desc-gray"
							content="Connect your Shopify store to install the ClickGUARD app and enable conversion tracking."
						>
							<AiOutlineQuestionCircle className="text-t-gray-hold h-4 w-4" />
						</GTooltip>
					</div>
				)}
				<div className="flex items-center gap-1 mt-3">
					<GRadio
						value={installType ?? ''}
						checked={installType === 'EMAIL'}
						onChange={() => setInstallType('EMAIL')}
						labelClass="font-normal text-t-google-tooltip text-base"
						label="Email my developer"
						name="email"
					/>
					<GTooltip
						position="top"
						arrowClassName="w-0 h-0 absolute bottom-[0.5px] border-l-8 border-l-transparent  border-r-8 border-r-transparent border-t-8 border-t-tooltip-caret"
						contentClassName="border-none font-grotesk mb-2 bg-t-smoke w-[300px] font-medium text-sm text-t-desc-gray"
						content="Email the tracking code instructions to your website manager."
					>
						<AiOutlineQuestionCircle className="text-t-gray-hold h-4 w-4" />
					</GTooltip>
				</div>
			</div>
			)}
			{installType === 'GTM' && <GtmAccountSelector />}
			{installType === 'MANUAL' && (
				<div className="mt-4">
					<p className="text-t-checkbox-unactive text-base my-2">
						To install manually, put the following code in the &lt;head&gt;
						section of your website
					</p>
					<TrackerCodeTemplate
						font="font-normal"
						isCopy={true}
						content={{
							subtitle: 'Tracking script',
							code:
								account?.id && asset?.id
									? trackingScript(account!.id, asset!.id, domainForScripts)
									: '< code sample >'
						}}
					/>
					<p className="text-t-checkbox-unactive text-base my-2">
						Make sure this code is added to all your website pages
					</p>
				</div>
			)}
			{shopifyEnabled && installType === 'SHOPIFY' && (
				<div className="mt-4">
					<ShopifyConnectSettings accountId={account!.id} assetId={asset!.id} initialLink={shopifyLink} />
				</div>
			)}
			{installType === 'EMAIL' && (
				<div className="mt-4">
					<p className="text-t-checkbox-unactive text-base mb-4">
						Email the tracking code installation instructions to your developer
						or to your domain manager.
					</p>
					<div className="mb-6 flex flex-col sm:flex-row gap-3 sm:items-end">
						<div className="w-full md:w-[60%]">
							<GInput
								label="Email"
								placeholder="Fill in a valid email"
								type="email"
								value={developerEmail}
								onChange={(e) => setDeveloperEmail(e.target.value)}
							/>
						</div>
						<GButton
							color="yellow"
							className="rounded-[8px] px-6 h-[40px] min-w-[90px]"
							label="Send"
							disabled={!developerEmail}
							onClick={async () => {
								if (!account?.id || !asset?.id) {
									toast.error('Account or asset information is missing')
									return
								}

								try {
									// Check if we're in onboarding/setup flow
									const isInSetup =
										account?.status === 'SETUP' ||
										account?.setup?.step !== 'SETUP_COMPLETE'
									if (isInSetup) {
										trackPostHogEvent(
											PostHogEvent.ONBOARDING_EMAIL_DEVELOPER,
											PostHogPath.ONBOARDING,
											null
										)
									} else {
										trackPostHogEvent(
											PostHogEvent.SEND_TRACKING_CODE_EMAIL,
											PostHogPath.SETTINGS_TC,
											null
										)
									}
									await Assets.sendTrackingCodeEmail(
										account.id,
										asset.id,
										developerEmail
									)
									toast.success('Tracking code email sent successfully!')
									setDeveloperEmail('')
								} catch (error: any) {
									toast.error(
										error?.response?.data?.message ||
											'Failed to send email. Please try again.'
									)
									console.error('Error sending tracking code email:', error)
								}
							}}
						/>
					</div>
				</div>
			)}
			{!hideVerification && (isTrackingVerificationDone &&
			trackingVerificationCompletedAt &&
			serverSideTrackingUrlDetected ? (
				<div className="flex items-center gap-3 mb-4">
					<RiCheckLine className="w-6 h-6 text-green-500" />
					<span className="text-base font-medium text-green-600">
						{hasAccessToServerSideTracking ? 'Advanced' : 'Standard'} tracking
						Verified on{' '}
						{trackingVerificationCompletedAt
							.toLocaleDateString('en-US', {
								month: 'short',
								day: 'numeric',
								year: 'numeric'
							})
							.toLowerCase()}
					</span>
					<GButton
						color="neutral"
						className="rounded-[8px] px-6 h-[40px] min-w-[150px] ml-2"
						label="Verify again"
						onClick={() => {
							setIsTrackingVerificationDone(false)
							setServerSideTrackingUrlDetected(false)
						}}
					/>
				</div>
			) : isTrackingVerificationDone &&
			  trackingVerificationCompletedAt &&
			  !serverSideTrackingUrlDetected ? (
				<div className="mb-4">
					{!hasDoneServerSideTracking ? (
						<div>
							<div className="flex items-center gap-3">
								<RiCheckLine className="w-6 h-6 text-green-500" />
								<span className="text-base font-medium text-green-600">
									Tracking code is installed successfully (Verified on{' '}
									{trackingVerificationCompletedAt
										.toLocaleDateString('en-US', {
											month: 'short',
											day: 'numeric',
											year: 'numeric'
										})
										.toLowerCase()}
									)
								</span>
							</div>
							<div className="flex gap-3 items-center mb-3">
								<div className="flex-1">
									<p className="text-md text-t-checkbox-unactive">
										Extend protection and maximize ROI by using{' '}
										<span className="font-bold">advanced tracking</span>.{' '}
										<a
											href={AppConfig.KNOWLEDGEBASE_URL}
											target="_blank"
											rel="noopener noreferrer"
											className="underline inline-flex items-center gap-1"
											onClick={() => {
												trackPostHogEvent(
													PostHogEvent.LINK_KNOWLEDGE_BASE,
													PostHogPath.SETTINGS_TC,
													null
												)
											}}
										>
											Learn more
											<HiExternalLink className="w-4 h-4" />
										</a>
									</p>
								</div>
							</div>
						</div>
					) : (
						<div className="flex gap-3 items-center mb-3">
							<span className="text-[#FABC04]">
								<PiWarningDiamondFill className="w-6 h-6" />
							</span>
							<div className="flex-1">
								<p className="text-md text-t-checkbox-unactive">
									<span className="font-bold">
										Tracking code detected - Action required
									</span>
									<br />
									Your website is using the{' '}
									<span className="font-bold">Standard</span> version of our
									tracking code.
									<br />
									Please <span className="font-bold">install</span> the{' '}
									<span className="font-bold">Advanced tracking code</span> and
									verify the installation again.{' '}
									<a
										href={AppConfig.KNOWLEDGEBASE_URL}
										target="_blank"
										rel="noopener noreferrer"
										className="underline inline-flex items-center gap-1"
										onClick={() => {
											trackPostHogEvent(
												PostHogEvent.LINK_KNOWLEDGE_BASE,
												PostHogPath.SETTINGS_TC,
												null
											)
										}}
									>
										Learn more
										<HiExternalLink className="w-4 h-4" />
									</a>
								</p>
							</div>
						</div>
					)}
					<GButton
						color="neutral"
						className="rounded-[8px] px-6 h-[40px] min-w-[150px]"
						label="Verify again"
						onClick={() => {
							setIsTrackingVerificationDone(false)
							setServerSideTrackingUrlDetected(false)
						}}
					/>
				</div>
			) : (
				<>
					<hr className="my-6 border-t border-gray-200" />
					<div className="flex flex-col sm:flex-row gap-3 sm:items-end max-w-[640px]">
						<div className="w-full sm:max-w-[420px]">
							<GInput
								label="Domain"
								labelClassName="text-base font-medium"
								className="flex flex-col gap-1"
								inputClassName="w-full text-base font-normal"
								placeholder="clientwebsite.com"
								value={websiteDomain}
								onChange={(e) => {
									const value = e.target.value
									setWebsiteDomain(value)
									// Validate domain
									if (value && !isValidDomain(value)) {
										setDomainError(
											'Please enter a valid domain (e.g., example.com)'
										)
									} else {
										setDomainError(null)
									}
								}}
								error={domainError || undefined}
							/>
						</div>
						<GButton
							color="neutral"
							className="rounded-[8px] px-6 h-[40px] min-w-[180px]"
							label="Verify installation"
							onClick={handleVerifyInstallation}
							loading={isVerifying}
							disabled={isVerifying}
						/>
					</div>
					{verificationStatus === 'verified' && (
						<p className="text-green-600 mt-4 text-sm">
							✓ Verification successful! The tracker script is installed.
						</p>
					)}
					{verificationStatus === 'error' && (
						<p className="text-red-600 mt-4 text-sm">
							Verification failed. Please make sure you have the tracker script
							installed on your website.
						</p>
					)}
					{isVerifying && (
						<p className="text-t-checkbox-unactive mt-4 text-sm">
							Verifying installation... Please wait while we check your website.
						</p>
					)}
				</>
			))}
			<p className="text-t-base-blue mt-6">
				Visit the{' '}
				<a
					className="mb-2 text-md text-t-email-blue hover:text-t-dark-blue font-bold"
					href={
						'https://docs.clickguard.com/en/articles/21430-how-to-install-the-clickguard-tracking-code'
					}
					target="_blank"
					onClick={() => {
						trackPostHogEvent(
							PostHogEvent.LINK_KNOWLEDGE_BASE,
							PostHogPath.SETTINGS_TC,
							null
						)
					}}
				>
					Knowledge base
				</a>{' '}
				for additional help.
			</p>
		</div>
	)
}

export const TrackerCodeTemplate = (props: {
	content: any
	badge?: any
	height?: number
	isCopy?: boolean
	font?: string
}) => {
	const { content, height, isCopy = true, font } = props
	const [copyStatus, setCopyStatus] = useLocalState(false)
	return (
		<div>
			<div className="flex gap-4 mt-2">
				<div className="relative w-full">
					<textarea
						rows={height || 3}
						cols={42}
						className={`text-t-desc-gray border-t-radio border rounded-lg text-sm bg-bg-plan-fill w-full py-4
							pl-4 pr-8  resize-none placeholder-gray-500 focus:ring-0  ${
								font || 'font-medium'
							}`}
						readOnly
						value={content.code}
					/>
					{isCopy && (
						<div
							className="absolute top-3.5 right-3 cursor-pointer"
							onClick={() => copy(content.code)}
						>
							<GCopyTooltip
								position="top"
								arrowClassName="w-0 h-0 absolute bottom-[0.5px] border-l-8 border-l-transparent  border-r-8 border-r-transparent border-t-8 border-t-tooltip-caret"
								contentClassName="border-none font-grotesk mb-2 bg-t-smoke min-w-max font-medium text-sm text-t-desc-gray"
								content={copyStatus ? 'Copied!' : 'Copy'}
							>
								<FiCopy className="w-5 h-5 text-t-google-tooltip hover:text-t-base-blue" />
							</GCopyTooltip>
						</div>
					)}
				</div>
			</div>
		</div>
	)

	async function copy(text: string) {
		await navigator.clipboard.writeText(text)
		setCopyStatus(true)
		setTimeout(() => {
			setCopyStatus(false)
		}, 2000)
	}
}
